import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, List, Tooltip } from '@material-ui/core';
import { ModalWrapper } from 'components/Modals';
import './EasterEggModal.scss';
import TokenIcon from 'assets/images/token.svg';
import EggHunterBadge from 'assets/images/badges/egg-hunter-badge.svg';
import { RootStore } from 'redux/store';
import moment from 'moment';
import EggHunterItem from 'components/EggHunterItem/EggHunterItem';
import { getMasterEggHunters } from 'redux/thunks';
import EasterEggListItem from 'components/EasterEggListItem/EasterEggListItem';
import { Loader } from 'components/Loader';
import { useEasterEggFound } from 'helpers/easterEgghelper';
import EASTER_EGG_IDS from 'helpers/easterEggConstants';

interface EasterEggProps {
	open: boolean;
	allowClickOutsideToClose?: boolean;
	onCloseClicked?: () => void;
}

interface EasterEggProgressObj {
	eggName: string;
	dateFound: string | null;
}

const EasterEgg: React.FC<EasterEggProps> = (props: EasterEggProps) => {
	const constraintsRef = useRef(null);
	const userNameState = useSelector((state: RootStore) => state.user.userName);
	const easterEggState = useSelector((state: RootStore) => state.EasterEgg);
	const amIMasterEggHunter = useSelector((state: RootStore) => state.EasterEgg.amIMasterEggHunter);
	const [eggProgressArr, setEggProgressArr] = useState<EasterEggProgressObj[]>([]);
	const [easterEggBadge, setEasterEggBadge] = useState(false);
	const easterEggFound = useEasterEggFound(EASTER_EGG_IDS.EASTER_EGG_BADGE);
	const { open, allowClickOutsideToClose, onCloseClicked } = props;
	const dispatch = useDispatch();

	const toggleEasterEggBadge = () => {
		setEasterEggBadge(!easterEggBadge);
		if (!easterEggBadge) {
			easterEggFound();
		}
	};

	useEffect(() => {
		if (open) {
			dispatch(getMasterEggHunters());
		}
		if (amIMasterEggHunter) {
			dispatch(getMasterEggHunters());
		}
	}, [open, dispatch, amIMasterEggHunter]);

	useEffect(() => {
		if (easterEggState.easterEggs && easterEggState.easterEggs.length > 0) {
			let userEggMap = new Map();
			let easterEggProgressArr: EasterEggProgressObj[] = [];
			easterEggState.userEggs.forEach((userEgg, index) => userEggMap.set(userEgg.eggId, index));

			easterEggState.easterEggs.forEach(egg => {
				let userEggIndex = userEggMap.get(egg.eggId);
				let dateFound = null;
				if (userEggIndex !== undefined) {
					dateFound = moment(easterEggState.userEggs[userEggIndex].dateFound).format('MMM DD, YYYY @ h:mm A');
				}

				let eggProgress: EasterEggProgressObj = {
					eggName: egg.eggName,
					dateFound: dateFound,
				};

				easterEggProgressArr.push(eggProgress);
			});

			setEggProgressArr(easterEggProgressArr);
		}
	}, [easterEggState]);

	return (
		<ModalWrapper
			height='tall'
			open={open}
			onCloseClicked={onCloseClicked}
			allowClickOutsideToClose={allowClickOutsideToClose}
			constraintsRef={constraintsRef}
		>
			<Grid container direction='column' alignItems='center' className='egg-hunt-modal'>
				<Tooltip title='🥚'>
					<img
						src={TokenIcon}
						onClick={() => toggleEasterEggBadge()}
						className={`${easterEggBadge ? 'token-icon transparent' : 'token-icon'}`}
						alt='toa-token'
					/>
				</Tooltip>

				<img
					src={EggHunterBadge}
					onClick={() => toggleEasterEggBadge()}
					className={`${easterEggBadge ? 'token-icon' : 'token-icon transparent'}`}
					alt='egg-hunter-badge'
				/>
				<div className='egg-hunt-modal__title'>Egg Hunt</div>
				<div className='egg-hunt-container'>
					{/* Master Egg Hunters List */}
					<div className='master-egg-hunters'>
						<h3 className='easter-egg__subtitle'>Master Egg Hunters</h3>
						{easterEggState.loadingEggHunters ? (
							<div className='list--loading'>
								<Loader />
							</div>
						) : (
							<List disablePadding={true} dense={true}>
								{easterEggState.eggHunters.map((hunter, index) => (
									<EggHunterItem hunter={hunter} itemNum={index + 1} key={index}></EggHunterItem>
								))}
								{easterEggState.eggHunters.length === 0 && <div className='no-egg-hunters-msg'>No one has found all of the easter eggs.</div>}
							</List>
						)}
					</div>
					{/* Current user's egg hunt progress */}
					<div className='my-egg-hunt'>
						<h3 className='easter-egg__subtitle'>{userNameState}</h3>
						<div className='my-egg-hunt__subtitle'>{`You've found ${easterEggState.userEggs.length} of ${easterEggState.easterEggs.length}`}</div>
						{easterEggState.loadingEasterEggs || easterEggState.loadingUserEasterEggs ? (
							<div className='list--loading'>
								<Loader />
							</div>
						) : (
							<List dense={true} className='my-egg-hunt__list'>
								{eggProgressArr.map(userEgg => (
									<EasterEggListItem eggName={userEgg.eggName} dateFound={userEgg.dateFound} key={userEgg.eggName}></EasterEggListItem>
								))}
							</List>
						)}
					</div>
				</div>
			</Grid>
		</ModalWrapper>
	);
};

export default EasterEgg;
